// RouteForm.tsx
import React, { useState, useEffect } from 'react'
import { ModalWrapper, SelectField, StyledButton, TextFields } from '../../../../../../components'
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useGlobal, usePostApi } from '../../../../../../shared'
import { useFormik } from 'formik'
import { setLoader, setRefreshPage } from '../../../../../../redux/globalSlice'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { baseURL, urls } from '../../../../../../shared/config'
import axios from 'axios'
import { InfoField } from '../../School/PopOver/SchoolInfo'
import { highestInt } from '../../../../../../shared/utils/constants'
import classes from './Route.module.css'

const validationSchema = Yup.object().shape({
  area: Yup.string().required('Area is required'),
})

export const RouteForm = ({
  open,
  handleClose,
  customRole,
  userSchoolId,
  update,
  step,
  setStep,
  refetch,
}) => {
  const { data: schoolList } = useGlobal({
    userUrl: `${urls.SCHOOL_LIST}?page=1&limit=${highestInt}`,
  })

  const data = useSelector((state) => state.global.SchoolDInfo)

  // console.log('---data',data);

  const userSchoolName = schoolList?.find((school) => school.school_id == userSchoolId)?.school_name

  const { postApi } = usePostApi()
  const dispatch = useDispatch()

  const initialValues = {
    area: '',
    schoolName: '',
    price: '',
    van: [], // array of van_ids
  }

  const handleSubmit = async () => {
    dispatch(setLoader(true))

    try {
      await validationSchema.validate(formik.values, { abortEarly: false })

      const responseData = {
        school_id: customRole === 'Admin' ? userSchoolId : formik.values.schoolName,
        route_name: formik.values.area,
        van_id: formik.values.van,
        fees: formik.values.price,
      }

      const updateData = {
        route_id: data?.route_id,
        school_id: customRole === 'Admin' ? userSchoolId : formik.values.school_id,
        route_name: formik.values.area,
        van_id: formik.values.van,
        fees: formik.values.price,
      }

      let response
      if (update) {
        response = await axios.patch(`${baseURL}${urls.ROUTE_PATCH}`, updateData)
      } else {
        response = await postApi(urls.ROUTE_POST, responseData)
      }

      if (response.status === 201 || response.status === 200) {
        toast.success(update ? 'Route Updated Successfully!' : 'Route Added Successfully!')
        dispatch(setRefreshPage(true))
        handleClose()
      } else {
        toast.error(response.message || 'Something went wrong')
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach((err) => toast.error(err.message))
      } else {
        toast.error('Something Went Wrong!')
        console.error(error)
      }
    } finally {
      dispatch(setLoader(false))
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  })

  // Fetch van data
  const { data: vanData } = useGlobal({
    userUrl: `${urls.VAN_GET}${customRole === 'Admin' ? userSchoolId : formik.values.schoolName}&page=1&limit=1000&${update ? `isAssign=true` : `isAssign=false`}&route_id=${data?.route_id || ''}`,
  })

  // Auto-select vans that have isAssign: true when component loads or vanData changes
  useEffect(() => {
    if (vanData && vanData.length > 0) {
      const preSelectedVans = vanData
        .filter((van) => van.isAssign === true) // ← Key change here
        .map((van) => van.van_id)

      // Only set if not already set (to avoid overwriting user changes in edit mode)
      if (formik.values.van.length === 0) {
        formik.setFieldValue('van', preSelectedVans)
      }
    }
  }, [vanData])

  // Populate form when editing
  React.useEffect(() => {
    if (data && update) {
      formik.setValues({
        schoolName: data?.school_name || '',
        school_id: data?.school_id,
        area: data?.route_name || '',
        price: data?.transport_fee || '',
        van: data?.van_id ? (Array.isArray(data.van_id) ? data.van_id : [data.van_id]) : [],
      })
    }
  }, [data, update])

  const handleNext = () => {
    if (step === 1) setStep(2)
  }

  const handleBack = () => {
    if (step > 1) setStep(1)
    else handleClose()
  }
  // Auto-select vans that are already assigned (isAssigned: true)
  React.useEffect(() => {
    if (vanData && Array.isArray(vanData) && vanData.length > 0) {
      const preSelected = vanData
        .filter((van) => van.isAssigned === true) // Note: capital 'A'
        .map((van) => van.van_id)

      // Only auto-select if user hasn't made any selection yet (prevents overwriting edits)
      if (formik.values.van.length === 0) {
        formik.setFieldValue('van', preSelected)
      }
    }
  }, [vanData, formik.values.van.length]) // Depend on vanData and current length

  return (
    <ModalWrapper
      open={open}
      handleClose={handleClose}
      width={'500px'}
      title={step === 1 ? `${update ? 'Update' : 'Add'} Route` : 'Assign Van'}
    >
      {step === 1 && (
        <div>
          {customRole === 'Admin' ? (
            <InfoField label={'School'} value={userSchoolName} width={'100%'} />
          ) : update ? (
            <InfoField label={'School'} value={formik.values.schoolName} width={'100%'} />
          ) : (
            <SelectField
              required
              label={'School'}
              options={(schoolList || [])?.map((prod) => ({
                value: prod.school_id,
                label: prod.school_name,
              }))}
              width={'100%'}
              name="schoolName"
              value={formik.values.schoolName}
              onChange={(selectedOption) => formik.setFieldValue('schoolName', selectedOption)}
            />
          )}

          <TextFields
            label={'Route'}
            required
            width={'100%'}
            name={'area'}
            value={formik.values.area}
            onChange={formik.handleChange}
          />

          <TextFields
            label={'Fees'}
            width={'100%'}
            name={'price'}
            value={formik.values.price}
            onChange={formik.handleChange}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ width: '50%' }} className="in-a-row">
              <Typography className="back-btn" onClick={handleBack}>
                Back
              </Typography>
              <StyledButton width={'40%'} onClick={handleNext}>
                Next
              </StyledButton>
            </Box>
          </div>
        </div>
      )}

      {step === 2 && (
        <>
          <Typography mb={1.25} className="custom-selectfield-label">
            Van List <span className="req">*</span>
          </Typography>

          <div className={classes.box}>
            {vanData?.map((van, index) => {
              const isChecked = formik.values.van?.includes(van.van_id)
              const isLastItem = index === vanData.length - 1

              return (
                <div
                  key={van.van_id}
                  style={{
                    borderBottom: isLastItem ? 'none' : '1px solid #eee',
                  }}
                  className={classes.subjrxt}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={() => {
                          if (isChecked) {
                            // Remove van
                            formik.setFieldValue(
                              'van',
                              formik.values.van.filter((id) => id !== van.van_id),
                            )
                          } else {
                            // Add van
                            formik.setFieldValue('van', [...formik.values.van, van.van_id])
                          }
                        }}
                      />
                    }
                    label={`${van.van_number} ${van.employee_name ? `(${van.employee_name})` : ''}`}
                    labelPlacement="end"
                  />
                </div>
              )
            })}

            {(!vanData || vanData.length === 0) && (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                No vans available
              </Typography>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <Box sx={{ width: '50%' }} className="in-a-row">
              <Typography className="back-btn" onClick={handleBack}>
                Back
              </Typography>
              <StyledButton width={'40%'} onClick={handleSubmit}>
                {update ? 'Update' : 'Add'}
              </StyledButton>
            </Box>
          </div>
        </>
      )}
    </ModalWrapper>
  )
}
